import { useModalContext } from '#pplx/frontend/aether/modals/context/useModalContext.js';
import { createContext, useCallback, useContext } from 'react';
const activeCleanups = new Map();
function drainActiveCleanups(skipKey) {
    for (const [key, cleanup] of activeCleanups.entries()) {
        if (key === skipKey)
            continue;
        cleanup();
        activeCleanups.delete(key);
    }
}
const CONNECTOR_CLEANUP_KEY = 'connector';
const FAMILY_CLEANUP_KEY = 'family';
export const ConnectorModalContext = createContext({
    stackModals: false,
});
ConnectorModalContext.displayName = 'ConnectorModalContext';
export function useConnectorModalContext() {
    const { openModal } = useModalContext();
    const { stackModals, onConnectorModalOpen, onFamilyModalOpen } = useContext(ConnectorModalContext);
    // Drain other lifecycles' cleanups before a non-stacked open clobbers them silently.
    const maybeDrain = useCallback((skipKey) => {
        if (stackModals)
            return;
        drainActiveCleanups(skipKey);
    }, [stackModals]);
    const wrappedOpenModal = useCallback((component, props) => {
        maybeDrain(null);
        // TODO(daniel.morgan): This is an anti-pattern but preserving for
        // backward compatibility. Options should be up to the caller
        return openModal(component, props, { replace: !stackModals });
    }, [maybeDrain, openModal, stackModals]);
    const openConnectorModal = useCallback((connectorName, component, props) => {
        maybeDrain(CONNECTOR_CLEANUP_KEY);
        const userOnClose = props.onClose;
        let cleanup;
        const result = openModal(component, {
            ...props,
            onClose: () => {
                cleanup?.();
                activeCleanups.delete(CONNECTOR_CLEANUP_KEY);
                userOnClose?.();
            },
        }, { replace: !stackModals });
        cleanup = onConnectorModalOpen?.(connectorName, result.closeModal);
        if (cleanup)
            activeCleanups.set(CONNECTOR_CLEANUP_KEY, cleanup);
        return result;
    }, [openModal, stackModals, onConnectorModalOpen, maybeDrain]);
    const openFamilyModal = useCallback((familyId, component, props) => {
        maybeDrain(FAMILY_CLEANUP_KEY);
        const userOnClose = props.onClose;
        let cleanup;
        const result = openModal(component, {
            ...props,
            onClose: () => {
                cleanup?.();
                activeCleanups.delete(FAMILY_CLEANUP_KEY);
                userOnClose?.();
            },
        }, { replace: !stackModals });
        cleanup = onFamilyModalOpen?.(familyId, result.closeModal);
        if (cleanup)
            activeCleanups.set(FAMILY_CLEANUP_KEY, cleanup);
        return result;
    }, [openModal, stackModals, onFamilyModalOpen, maybeDrain]);
    return { openModal: wrappedOpenModal, openConnectorModal, openFamilyModal };
}
//# sourceMappingURL=ConnectorModalContextShared.js.map